import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { api, ApiError } from '../lib/api.js';
import { clearPersisted, useDeadline, usePersistentState } from '../lib/persistentState.js';
import { EmailField } from './EmailField.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import { Icon } from './Icons.js';
import { useTheme } from './ThemeProvider.js';

/**
 * Everything reachable without a session: signing in, recovering a password,
 * and asking for an account.
 *
 * One component rather than three routes, because all three are the same card
 * in the same place and the user moves between them by changing their mind, not
 * by navigating. Keeping them together also keeps the email address they typed
 * when they switch from "sign in" to "I forgot".
 */

type Screen = 'login' | 'forgot' | 'signup';

function formatClock(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

export function LoginPanel({ onLoggedIn }: { onLoggedIn: () => void }) {
  // Persisted, not local: a background refetch, a discarded tab or an accidental
  // refresh must not drop somebody back at a blank sign-in form holding a code
  // they cannot request again until it expires.
  const [screen, setScreen] = usePersistentState<Screen>('mpr.login.screen', 'login');
  // Shared across screens on purpose: someone who mistyped their password and
  // clicks "forgot" should not have to type their address again.
  const [email, setEmail] = usePersistentState('mpr.login.email', '');

  const { t, toggleLanguage } = useI18n();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="login-wrap">
      <div className="card login">
        <div className="brand">
          <span className="brand-mark">
            <Icon.send />
          </span>
          {t('app.name')}
        </div>

        {screen === 'login' && (
          <SignIn
            email={email}
            setEmail={setEmail}
            onLoggedIn={onLoggedIn}
            onForgot={() => setScreen('forgot')}
            onSignup={() => setScreen('signup')}
          />
        )}

        {screen === 'forgot' && (
          <ForgotPassword
            email={email}
            setEmail={setEmail}
            onDone={() => {
              clearPersisted('mpr.forgot.sent', 'mpr.forgot.deadline');
              setScreen('login');
            }}
          />
        )}

        {screen === 'signup' && (
          <SignUp
            onDone={() => {
              clearPersisted('mpr.signup.form', 'mpr.signup.stage', 'mpr.signup.deadline');
              setScreen('login');
            }}
          />
        )}

        {/* Reachable before signing in — someone who cannot read the form
            should still be able to switch language. */}
        <div className="login-lang">
          <button type="button" className="ghost small" onClick={toggleLanguage}>
            <Icon.globe />
            {t('app.language')}
          </button>
          <button
            type="button"
            className="ghost small"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? t('app.theme.toLight') : t('app.theme.toDark')}
          >
            {theme === 'dark' ? <Icon.sun /> : <Icon.moon />}
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Sign in                                                                    */
/* -------------------------------------------------------------------------- */

function SignIn(props: {
  email: string;
  setEmail: (value: string) => void;
  onLoggedIn: () => void;
  onForgot: () => void;
  onSignup: () => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  /** Off on arrival: the default has to be the private one. */
  const [revealed, setRevealed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.login(props.email, password);
      /*
       * Before a single query of the new session runs. A browser shared between
       * two operators — or one person keeping a second account open to test
       * with — must not carry anything across that boundary, and this is the
       * earliest moment at which the boundary is known to have been crossed.
       */
      queryClient.clear();
      clearPersisted(
        'mpr.login.screen',
        'mpr.login.email',
        'mpr.forgot.sent',
        'mpr.forgot.deadline',
        'mpr.signup.form',
        'mpr.signup.stage',
        'mpr.signup.deadline',
      );
      props.onLoggedIn();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t('login.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <h2>{t('login.title')}</h2>
      <p className="muted small login-sub">{t('login.subtitle')}</p>

      <label htmlFor="email">{t('login.email')}</label>
      <EmailField id="email" value={props.email} onChange={props.setEmail} required />

      <label htmlFor="password">{t('login.password')}</label>
      {/* A password nobody can read is a password typed twice. The control sits
          inside the field rather than under it, so it does not read as another
          thing to fill in — and it says what pressing it will do, not what the
          field is doing now, which is the half everybody gets backwards. */}
      <div className="password-field">
        <input
          id="password"
          type={revealed ? 'text' : 'password'}
          className="ltr"
          value={password}
          autoComplete="current-password"
          onChange={(event) => setPassword(event.target.value)}
          required
        />
        <button
          type="button"
          className="link tiny"
          onClick={() => setRevealed(!revealed)}
          aria-pressed={revealed}
        >
          {revealed ? t('login.hidePassword') : t('login.showPassword')}
        </button>
      </div>

      {error && <p className="error small">{error}</p>}

      <button type="submit" className="primary" disabled={busy}>
        {busy ? t('login.submitting') : t('login.submit')}
      </button>

      <div className="login-links">
        <button type="button" className="link" onClick={props.onForgot}>
          {t('login.forgot')}
        </button>
        <button type="button" className="link" onClick={props.onSignup}>
          {t('login.newAccount')}
        </button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/*  Forgotten password                                                         */
/* -------------------------------------------------------------------------- */

function ForgotPassword(props: {
  email: string;
  setEmail: (value: string) => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const [code, setCode] = useState('');
  // Passwords are never persisted — only the fact that a code is outstanding.
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [notice, setNotice] = useState<{
    kind: 'ok' | 'error';
    text: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = usePersistentState('mpr.forgot.sent', false);
  const {
    remaining,
    start: startCountdown,
    clear: clearCountdown,
  } = useDeadline('mpr.forgot.deadline');

  const requestCode = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const result = await api.requestPasswordReset(props.email);
      setSent(true);
      // Counting down the server's own number, so "resend" unlocks at exactly
      // the moment the code it would replace expires.
      startCountdown(result.ttlSeconds);
      setNotice({ kind: 'ok', text: t('forgot.codeSent') });
    } catch (cause) {
      setNotice({
        kind: 'error',
        text: cause instanceof ApiError ? cause.message : t('common.requestFailed'),
      });
    } finally {
      setBusy(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (newPassword !== confirm) {
      setNotice({ kind: 'error', text: t('forgot.mismatch') });
      return;
    }

    setBusy(true);
    setNotice(null);
    try {
      await api.completePasswordReset(props.email, code, newPassword);
      setNotice({ kind: 'ok', text: t('forgot.done') });
      // The code is spent and the flow is over; nothing left worth restoring.
      clearCountdown();
      clearPersisted('mpr.forgot.sent', 'mpr.login.screen');
      setTimeout(props.onDone, 1500);
    } catch (cause) {
      setNotice({
        kind: 'error',
        text: cause instanceof ApiError ? cause.message : t('common.requestFailed'),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <h2>{t('forgot.title')}</h2>
      <p className="muted small login-sub">{t('forgot.subtitle')}</p>

      <label htmlFor="f-email">{t('login.email')}</label>
      <div className="field-with-action">
        <EmailField id="f-email" value={props.email} onChange={props.setEmail} required />
        <button
          type="button"
          className="secondary"
          onClick={requestCode}
          // Locked for as long as the code already in their inbox is valid:
          // a second code would silently invalidate the first, which is the
          // most confusing thing this screen could do.
          disabled={busy || !props.email || remaining > 0}
        >
          {remaining > 0 ? formatClock(remaining) : t('forgot.sendCode')}
        </button>
      </div>

      {sent && (
        <>
          <label htmlFor="f-code">{t('forgot.code')}</label>
          <input
            id="f-code"
            className="ltr code-input"
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            required
          />

          <label htmlFor="f-new">{t('forgot.newPassword')}</label>
          <input
            id="f-new"
            type="password"
            className="ltr"
            value={newPassword}
            autoComplete="new-password"
            minLength={6}
            onChange={(event) => setNewPassword(event.target.value)}
            required
          />

          <label htmlFor="f-confirm">{t('forgot.confirmPassword')}</label>
          <input
            id="f-confirm"
            type="password"
            className="ltr"
            value={confirm}
            autoComplete="new-password"
            minLength={6}
            onChange={(event) => setConfirm(event.target.value)}
            required
          />
        </>
      )}

      {notice && <p className={notice.kind === 'ok' ? 'ok small' : 'error small'}>{notice.text}</p>}

      {sent && (
        <button type="submit" className="primary" disabled={busy || code.length !== 6}>
          {t('forgot.submit')}
        </button>
      )}

      <div className="login-links">
        <button type="button" className="link" onClick={props.onDone}>
          {t('forgot.backToLogin')}
        </button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/*  Sign up                                                                    */
/* -------------------------------------------------------------------------- */

function SignUp(props: { onDone: () => void }) {
  const { t } = useI18n();
  const [form, setForm] = usePersistentState('mpr.signup.form', {
    organisationName: '',
    contactName: '',
    email: '',
    phone: '',
  });
  const [code, setCode] = useState('');
  const [stage, setStage] = usePersistentState<'details' | 'verify' | 'submitted'>(
    'mpr.signup.stage',
    'details',
  );
  const [notice, setNotice] = useState<{
    kind: 'ok' | 'error';
    text: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const {
    remaining,
    start: startCountdown,
    clear: clearCountdown,
  } = useDeadline('mpr.signup.deadline');

  const requestCode = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const result = await api.requestSignup(form);
      setStage('verify');
      startCountdown(result.ttlSeconds);
      setNotice({ kind: 'ok', text: t('signup.codeSent') });
    } catch (cause) {
      setNotice({
        kind: 'error',
        text: cause instanceof ApiError ? cause.message : t('common.requestFailed'),
      });
    } finally {
      setBusy(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      await api.verifySignup(form.email, code);
      setStage('submitted');
      clearCountdown();
    } catch (cause) {
      setNotice({
        kind: 'error',
        text: cause instanceof ApiError ? cause.message : t('common.requestFailed'),
      });
    } finally {
      setBusy(false);
    }
  };

  if (stage === 'submitted') {
    return (
      <div>
        <h2>{t('signup.pendingTitle')}</h2>
        {/* Says a person will decide, and that the answer comes by email. A
            bare "thank you" reads as a form that went nowhere. */}
        <p className="muted small login-sub">{t('signup.pendingBody')}</p>
        <button type="button" className="primary" onClick={props.onDone}>
          {t('forgot.backToLogin')}
        </button>
      </div>
    );
  }

  const detailsComplete =
    form.organisationName.length > 1 &&
    form.contactName.length > 1 &&
    form.email.includes('@') &&
    form.phone.length > 5;

  return (
    <form onSubmit={submit}>
      <h2>{t('signup.title')}</h2>
      <p className="muted small login-sub">{t('signup.subtitle')}</p>

      <label htmlFor="s-org">{t('signup.organisation')}</label>
      <input
        id="s-org"
        value={form.organisationName}
        onChange={(event) => setForm({ ...form, organisationName: event.target.value })}
        disabled={stage === 'verify'}
        required
      />

      <label htmlFor="s-name">{t('signup.contactName')}</label>
      <input
        id="s-name"
        value={form.contactName}
        onChange={(event) => setForm({ ...form, contactName: event.target.value })}
        disabled={stage === 'verify'}
        required
      />

      <label htmlFor="s-email">{t('login.email')}</label>
      <EmailField
        id="s-email"
        value={form.email}
        onChange={(email) => setForm({ ...form, email })}
        disabled={stage === 'verify'}
        required
      />

      <label htmlFor="s-phone">{t('signup.phone')}</label>
      <input
        id="s-phone"
        type="tel"
        className="ltr"
        placeholder="+967 7XXXXXXXX"
        value={form.phone}
        onChange={(event) => setForm({ ...form, phone: event.target.value })}
        disabled={stage === 'verify'}
        required
      />

      {stage === 'verify' && (
        <>
          <label htmlFor="s-code">{t('signup.code')}</label>
          <div className="field-with-action">
            <input
              id="s-code"
              className="ltr code-input"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
              required
            />
            <button
              type="button"
              className="secondary"
              onClick={requestCode}
              disabled={busy || remaining > 0}
            >
              {remaining > 0 ? formatClock(remaining) : t('forgot.resend')}
            </button>
          </div>
        </>
      )}

      {notice && <p className={notice.kind === 'ok' ? 'ok small' : 'error small'}>{notice.text}</p>}

      {stage === 'details' ? (
        <button
          type="button"
          className="primary"
          onClick={requestCode}
          disabled={busy || !detailsComplete}
        >
          {t('signup.sendCode')}
        </button>
      ) : (
        <button type="submit" className="primary" disabled={busy || code.length !== 6}>
          {t('signup.submit')}
        </button>
      )}

      <div className="login-links">
        <button type="button" className="link" onClick={props.onDone}>
          {t('forgot.backToLogin')}
        </button>
      </div>
    </form>
  );
}
